/**
 * Which links the app header offers on a page, and in what order.
 *
 * Lifted out of src/components/Header.tsx's renderNavItems (design-system §7b rule 1, 2026-09-30) so today's header
 * and the new design's app header offer the same links by the same rule. Today's header keeps its own markup (icons,
 * icon-only and tooltips); it asks this function only which links to draw. The rule, unchanged:
 *
 * - on a signed-in "user page" (the desktop, wallet, activity, profile, settings, notifications, subscription,
 *   integrations and /user/*) the public pages step back: insights, pricing and home are not offered, and on a phone
 *   team and mission are not either;
 * - the signed-in links follow the public ones: desktop, then wallet and activity for a member whose role shows them
 *   (usageVisibility's fail-open rule, read by the caller);
 * - the page you are on is not offered. "Pricing" has no current-page rule of its own (today's header never hid it on
 *   /pricing, a page that hides the header anyway); that is kept as it was.
 */
export type AppNavKey = 'team' | 'mission' | 'insights' | 'pricing' | 'desktop' | 'wallet' | 'activity' | 'home';

export interface AppNavLink {
  key: AppNavKey;
  to: string;
  label: string;
}

export interface AppNavInput {
  /** The page's path (location.pathname). */
  path: string;
  isAuthenticated: boolean;
  canSeeWallet: boolean;
  canSeeActivity: boolean;
  /** The phone's menu rather than the desktop band. */
  phone: boolean;
}

/** A signed-in person's own pages, where the public links step back. */
export function isUserPage(path: string): boolean {
  return path.startsWith('/user/')
    || path === '/wallet'
    || path === '/activity'
    || path === '/profile'
    || path === '/settings'
    || path === '/desktop'
    || path === '/notifications'
    || path === '/subscription'
    || path === '/integrations'
    || path.startsWith('/integrations/');
}

/** Is `key`'s page the one on screen (so it is not offered)? */
function isCurrent(key: AppNavKey, path: string): boolean {
  switch (key) {
    case 'home': return path === '/';
    case 'desktop': return path === '/desktop';
    case 'insights': return path.startsWith('/insights') || path.startsWith('/blog');
    case 'wallet': return path.startsWith('/wallet') || path.startsWith('/user/wallet');
    case 'pricing': return false;
    default: return path.startsWith(`/${key}`); // team, mission, activity
  }
}

/** Every link, in the band's order. Each one's word is its key; its page is `/<key>`, save home's. */
const ORDER: readonly AppNavKey[] = ['team', 'mission', 'insights', 'pricing', 'desktop', 'wallet', 'activity', 'home'];

/** The links to draw, in order. */
export function appNavLinks({ path, isAuthenticated, canSeeWallet, canSeeActivity, phone }: AppNavInput): AppNavLink[] {
  const userPage = isUserPage(path);
  const offered = (key: AppNavKey): boolean => {
    if (isCurrent(key, path)) return false;
    switch (key) {
      case 'team':
      case 'mission': return !userPage || !phone;
      case 'desktop': return isAuthenticated;
      case 'wallet': return isAuthenticated && canSeeWallet;
      case 'activity': return isAuthenticated && canSeeActivity;
      default: return !userPage; // insights, pricing, home
    }
  };
  return ORDER.filter(offered).map((key) => ({ key, to: key === 'home' ? '/' : `/${key}`, label: key }));
}
